import { debounce } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { testURL } from "../../../lib/TestURL";
import { testAccountName } from "../../../lib/testAccountName";
import { suggestAccountName } from "../../../lib/suggestAccountName";
import useBaseURL from "../../../config/useBaseUrl.js";

/**
 * Razlog koji je server naveo, umesto našeg „Network response was not ok".
 *
 * Provere polja vraćaju 400 sa `details[].msg` („Server is not active…",
 * „That node belongs to a different network…"), a ostale greške sa `message`.
 * Do 24.09.2026. se sve to bacalo i pisalo „Network response was not ok" — pa
 * je jedan pogrešan podrazumevani URL tražen dva sata, iako je server sve
 * vreme tačno govorio šta ne valja.
 */
const razlogSaServera = async (odgovor, rezerva) => {
  try {
    const telo = await odgovor.json();
    return telo?.details?.[0]?.msg || telo?.message || rezerva;
  } catch {
    return rezerva;
  }
};

export const useValidateAccount = () => {
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isValidAccount, setIsValidAccount] = useState(undefined);
  const [checkAccountErrorMSG, setCheckAccountErrorMSG] = useState(null);
  const [isLoadingCheckAccount, setIsLoadingCheckAccount] = useState(false);
  const baseURL = useBaseURL();

  // `useMemo`, ne `useCallback`: ovde se pamti VREDNOST koju `debounce` vrati,
  // a ne funkcija koju smo sami napisali. Sa `useCallback` linter ne može da
  // vidi šta ta vrednost zatvara u sebi — i s pravom je to prijavljivao.
  const validateAccount = useMemo(
    () => debounce(async (account, serverUrl) => {
      if (!account) {
        setIsValidAccount(false);
        setIsDuplicate(false);
        setCheckAccountErrorMSG("Blockchain account name is required");
        return;
      }

      if (!testAccountName(account)) {
        setIsValidAccount(false);
        setIsDuplicate(false);
        // Pravila se IMENUJU. „Invalid format" je korisnika ostavljalo da
        // pogađa da su velika slova, cifre 0 i 6–9, razmaci i crtice zabranjeni
        // — a ništa na ekranu to nije govorilo.
        setCheckAccountErrorMSG(
          "Use only a–z, digits 1–5 and dots, up to 12 characters, not starting or ending with a dot",
        );
        return;
      }

      setIsValidAccount(true);
      setIsLoadingCheckAccount(true);

      const url = `${baseURL}/api/account/account_exists`;
      const requestData = { account_name: account, server_url: serverUrl };

      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        });

        if (!response.ok) {
          throw new Error(
          await razlogSaServera(response, "Network response was not ok"),
        );
        }

        const data = await response.json();
        const isDuplicateAccount = data;

        setIsDuplicate(isDuplicateAccount);
        // Zauzet nalog nije ćorsokak nego račva: registracija nudi prisvajanje
        // uz privatni ključ (`AccountExists`). Poruka to i kaže, umesto da zvuči
        // kao odbijanje.
        setCheckAccountErrorMSG(
          isDuplicateAccount
            ? "This account already exists on the chain. You can claim it with its private key."
            : null,
        );
      } catch (error) {
        setIsDuplicate(false);
        setCheckAccountErrorMSG(error.message || "An error occurred");
      } finally {
        setIsLoadingCheckAccount(false);
      }
    }, 1000),
    [baseURL],
  );

  return {
    validateAccount,
    isDuplicate,
    isValidAccount,
    checkAccountErrorMSG,
    isLoadingCheckAccount,
  };
};
/**
 * Ponuda imena naloga, izvedena iz GUI imena.
 *
 * Slobodnost se proverava PRE nego što ime uđe u polje, i to je razlog zašto
 * ovo nije samo `suggestAccountName`: nalog koji na lancu već postoji otvara
 * `AccountExists`, prozor koji traži privatni ključ. Ponuđeno ime koje korisnik
 * nije tražio ne sme da ga odvede tamo. Zauzeto -> ćuti se.
 *
 * Ne dira `checkAccountErrorMSG` ni `isValidAccount`: ponuda nije korisnikov
 * unos, pa ne sme da ispisuje greške o nečemu što on nije otkucao.
 */
export const useSuggestAccountName = () => {
  const [suggestion, setSuggestion] = useState("");
  const baseURL = useBaseURL();

  const suggest = useMemo(
    () => debounce(async (guiname, serverUrl) => {
      const kandidat = suggestAccountName(guiname);
      if (!kandidat || !serverUrl) {
        setSuggestion("");
        return;
      }
      try {
        const response = await fetch(`${baseURL}/api/account/account_exists`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ account_name: kandidat, server_url: serverUrl }),
        });
        if (!response.ok) throw new Error("Network response was not ok");
        setSuggestion((await response.json()) ? "" : kandidat);
      } catch (error) {
        // Nedostupan čvor nije razlog da se ponudi ime koje možda postoji.
        setSuggestion("");
      }
    }, 500),
    [baseURL],
  );

  return { suggestion, suggest };
};

export const useValidateIDBG = () => {
  const [isValid, setIsValid] = useState(undefined);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const baseURL = useBaseURL();

  const validateIDBG = useCallback(
    async (idbg, serverUrl) => {
      if (!idbg) {
        setIsValid(false);
        setErrorMessage("Account name is required");
        return;
      }
      if (idbg.length < 3) {
        setIsValid(false);
        setErrorMessage("Account name is less than minimum length");
        return;
      }
      if (idbg.length > 50) {
        setIsValid(false);
        setErrorMessage("Account name exceeds maximum length");
        return;
      }

      const url = `${baseURL}/api/inerygui/guiname_exists`;
      const requestData = { guiname: idbg, server_url: serverUrl };

      setIsLoading(true);

      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        });

        if (!response.ok) {
          throw new Error(
          await razlogSaServera(response, "Network response was not ok"),
        );
        }

        const data = await response.json();
        const isDuplicate = data === true;
        const validationStatus = !isDuplicate;
        setIsValid(validationStatus);
        setErrorMessage(isDuplicate ? "Account name already exists" : null);
      } catch (error) {
        setIsValid(false);
        setErrorMessage(error.message || "An error occurred");
      } finally {
        setIsLoading(false);
      }
    }, // Debounce delay of 300ms
    [baseURL], // Dependencies
  );

  return {
    validateIDBG,
    isIDBGValid: isValid,
    idbgErrMSG: errorMessage,
    isIDBGLoading: isLoading,
  };
};

export const useValidateServer = () => {
  const [isServerValid, setIsServerValid] = useState(undefined);
  const [validateServerErrorMSG, setValidateServerErrorMSG] = useState(null);
  const [isLoadingValidateServer, setIsLoadingValidateServer] = useState(false);
  const baseURL = useBaseURL();

  const validateServer = useCallback(
    async (serverURL) => {
      // Ovde je do 24.09.2026. stajalo „ako je adresa podrazumevana, valjana
      // je" — bez ijedne provere. Zato je polje na TAS-u izgledalo ispravno
      // dok je svaka druga provera padala: upravo ta adresa je bila pogrešna.
      // Podrazumevana vrednost nema nikakvu prednost; proverava se kao i svaka.
      if (!serverURL) {
        setIsServerValid(false);
        setValidateServerErrorMSG("Server URL is required");
        return;
      }

      if (!testURL(serverURL)) {
        setIsServerValid(false);
        setValidateServerErrorMSG("Invalid URL format");
        return;
      }

      const url = `${baseURL}/api/inerygui/server_valid`;
      const requestData = { server_url: serverURL };

      setIsLoadingValidateServer(true);

      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        });

        if (!response.ok) {
          throw new Error(
            await razlogSaServera(response, "Network response was not ok"),
          );
        }

        const validationStatus = await response.json();

        setIsServerValid(validationStatus);
        setValidateServerErrorMSG(
          validationStatus ? null : "Server is not valid",
        );
      } catch (error) {
        setIsServerValid(false);
        setValidateServerErrorMSG(
          error.message || "An error occurred during validation",
        );
      } finally {
        setIsLoadingValidateServer(false);
      }
    },
    [baseURL],
  );

  const resetState = useCallback(() => {
    setIsServerValid(undefined);
    setValidateServerErrorMSG(null);
  }, [setIsServerValid, setValidateServerErrorMSG]);

  return {
    validateServer,
    resetState,
    isServerValid,
    validateServerErrorMSG,
    isLoadingValidateServer,
  };
};

export const useHandleFormSubmit = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const baseURL = useBaseURL();

  const handleFormSubmit = async (formData) => {
    const url = `${baseURL}/api/inerygui/register`;

    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(
          await razlogSaServera(response, "Network response was not ok"),
        );
      }

      const data = await response.json();
      setData(data);
    } catch (error) {
      console.log(error);
      setError(error.message || "An error occurred during registration");
    } finally {
      setIsLoading(false);
    }
  };

  const resetStates = () => {
    setData(null);
    setError(null);
    setIsLoading(false);
  };

  return {
    handleFormSubmit,
    formDataResponse: data,
    formResponseError: error,
    isFormResponseLoading: isLoading,
    resetFormState: resetStates,
  };
};
